import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Video } from "lucide-react";
import { SkycamPlayer } from "../components/SkycamPlayer";
import { SkycamNetworkMap } from "../components/SkycamNetworkMap";
import { SkycamCard, SkycamDirectory } from "../components/SkycamDirectory";
import { usePublishedSkycams, useSkycamNetwork } from "../hooks/useSkycamNetwork";

const tabClass = (on) => `mono h-9 border-b-2 px-3 text-[10px] font-bold uppercase tracking-widest transition-colors ${on ? "border-cyan-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`;

const Skycams = () => {
  const [params, setParams] = useSearchParams();
  const picks = usePublishedSkycams();
  const { stations, summary, loading, userLocation, locate, locating } = useSkycamNetwork();
  const [tab, setTab] = useState("picks");
  const [selectedId, setSelectedId] = useState(params.get("cam"));

  const byId = useMemo(() => {
    const map = new Map();
    stations.forEach((station) => map.set(station.id, station));
    picks.forEach((cam) => map.set(cam.station, { ...(map.get(cam.station) || {}), ...cam, id: cam.station, online: map.get(cam.station)?.online ?? true }));
    return map;
  }, [stations, picks]);

  useEffect(() => {
    if (!selectedId && (picks.length || stations.length)) setSelectedId(picks[0]?.station || stations[0]?.id);
  }, [selectedId, picks, stations]);
  useEffect(() => { if (picks.length === 0 && !loading) setTab("all"); }, [picks.length, loading]);

  const selected = selectedId ? byId.get(selectedId) || null : null;
  const select = (station) => { setSelectedId(station.id || station.station); setParams({ cam: station.id || station.station }, { replace: true }); };

  return (
    <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6" data-testid="skycams-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-cyan-400">
            <Video className="h-3.5 w-3.5" /> HazCam Network{summary ? ` · ${summary.online} cameras live` : ""}
          </div>
          <h1 className="display-italic sd-title-gradient mt-1 text-4xl uppercase sm:text-5xl">Storm Desk skycams</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Live sky cameras from across the HazCam network, streamed right here on Storm Desk. Pick a camera, search a city, or find the one nearest you.
          </p>
        </div>
        <div className="mono flex items-center gap-2 border border-white/10 bg-white/[0.03] px-3 py-2 text-[10px] uppercase tracking-widest text-slate-300" data-testid="skycams-network-summary">
          <MapPin className="h-3.5 w-3.5 text-cyan-400" /> {loading ? "Loading network…" : `${stations.length} stations · ${picks.length} Storm Desk picks`}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black" style={{ aspectRatio: "16 / 9" }} data-testid="skycams-featured">
            {selected ? (
              <SkycamPlayer cam={selected} testId="skycams-featured-player" />
            ) : (
              <div className="grid h-full place-items-center text-center" data-testid="skycams-empty-state">
                <div>
                  <Video className="mx-auto h-8 w-8 text-slate-600" />
                  <div className="mt-3 font-semibold text-slate-100">{loading ? "Connecting to the HazCam network…" : "No cameras available right now"}</div>
                </div>
              </div>
            )}
          </div>
          {selected && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2" data-testid="skycams-selected-meta">
              <div>
                <div className="text-lg font-bold text-white">{selected.name}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
                  {selected.network || selected.location || "HazCam station"}{selected.viewers != null ? ` · ${selected.viewers} watching` : ""}{selected.distance_km != null ? ` · ${Math.round(selected.distance_km)} km away` : ""}
                </div>
              </div>
              {picks.some((cam) => cam.station === selected.id) && <span className="mono border border-cyan-400/40 bg-cyan-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-cyan-200">Storm Desk pick</span>}
            </div>
          )}

          <div className="mt-4 overflow-hidden rounded-2xl border border-white/10" style={{ height: 320 }} data-testid="skycams-network-map">
            <SkycamNetworkMap stations={stations} selected={selected} onSelect={select} userLocation={userLocation} />
          </div>
        </div>

        <div className="flex min-h-[420px] flex-col xl:h-[calc(100vh-220px)] xl:min-h-[640px]">
          <div className="mb-3 flex border-b border-white/10">
            <button type="button" data-testid="skycams-tab-picks" onClick={() => setTab("picks")} className={tabClass(tab === "picks")}>Storm Desk picks ({picks.length})</button>
            <button type="button" data-testid="skycams-tab-all" onClick={() => setTab("all")} className={tabClass(tab === "all")}>All cameras</button>
          </div>
          {tab === "picks" ? (
            <div className="grid min-h-0 flex-1 content-start gap-2 overflow-y-auto pr-1 sm:grid-cols-2 xl:grid-cols-1" data-testid="skycams-picks-list">
              {picks.length === 0 && <div className="sd-panel rounded-xl p-6 text-center text-sm text-slate-400" data-testid="skycams-picks-empty">The Storm Desk team hasn’t featured cameras yet — browse all cameras instead.</div>}
              {picks.map((cam) => <SkycamCard key={cam.id} station={{ ...cam, id: cam.station, online: byId.get(cam.station)?.online }} selected={selected?.id === cam.station} onSelect={select} badge="Pick" testId={`skycam-card-${cam.id}`} />)}
            </div>
          ) : (
            <SkycamDirectory stations={stations} selected={selected} onSelect={select} locate={locate} locating={locating} userLocation={userLocation} loading={loading} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Skycams;
